import {
    SessionNote,
    OverreadStatusEnum,
    User,
    CoughAnalysisSetsFixedCommentsDisplay,
    CoughAnalysisSetComment
} from "../../../misc/api/apiTypes";
import { PeaksJs, PeaksPoint, PeaksPeriod } from "../interfacesPeaks/PeaksJS";
import { AnalysisMode } from "../store";
import {
    formatToHHmmss_sss,
    subtractFormattedTimesWithThousandsOfSec
} from "../../../misc/timeFormatting";
import { CoughAnalysisSet as CoughAnalysisSetClient, FixedComment, StudyCoughEventType } from "webportalclient";
import { RecordingPositionFilesData } from "@vitalograph/coughmarshalclient";

export enum PlayStateEnum {
    Playing,
    Paused,
    NotStarted
}

export enum EditModeEnum {
    NotEditing,
    EditingPoint,
    EditingPeriod
}

export enum LoadingStateEnum {
    Initial = "Initial State",
    SessionComments = "Session Comments",
    AnalysisSets = "Analysis Sets",
    EventTypes = "Event Types",
    Events = "Events",
    Waveforms = "Waveforms",
    PlayheadPosition = "Playhead Position",
    Loaded = "Loaded",
    HourSegments = "Hour Segments",
    Positions = "Positions Mapping"
}

export enum WaveformType {
    CWFT_RAW = "CWFT_RAW",
    CWFT_PROCESSED = "CWFT_PROCESSED",
    CWFT_SENSOR_PROCESSED = "CWFT_SENSOR_PROCESSED",
    CWFT_VTA_CT1 = "CWFT_VTA_CT1"
}

export const ToWaveformNumeric = (waveformType: WaveformType) => {
    switch (waveformType) {
        case WaveformType.CWFT_RAW:
            return 0;
        case WaveformType.CWFT_PROCESSED:
            return 1;
        case WaveformType.CWFT_SENSOR_PROCESSED:
            return 2;
        case WaveformType.CWFT_VTA_CT1:
            return 3;
    }
}

export interface CoughAnalysisSetType {
    Id: string;
    Name: string;
}

export interface CoughAnalysisSet {
    Id: string;
    SessionId: string;
    CreatedDateTime: string;
    ModifiedDateTime: string;
    Type: CoughAnalysisSetType;
    IsSelected: boolean;
    FixedCommentIds: string[];
    Analyst: User;
    StatusId: string;
    DisplayOrder: number;
}


export interface SessionMetadata {
    study: string;
    siteNumber: string;
    subjectNumber: string;
    technician: string;
    visitName: string;
    sessionId: string;
    sessionName: string;
    rawDuration?: number;
    processedDuration?: number;
    sensorProcessedDuration?: number;
}

export interface CoughOverreadFixedComment extends FixedComment {
    IsSelected: boolean;
}

export interface AnalyseCoreState {
    studyId: string;
    playbackServiceStatus: string;
    sessionMetadata: SessionMetadata;
    previousPlaybackTimepoint: number;
    mode: AnalysisMode;
    isViewOnly: boolean;
    loadingState: LoadingStateEnum;
    isLoadError: boolean;
    configErrorMessage: string;
    isEditMode: EditModeEnum;
    playState: PlayStateEnum;
    isMicrophoneMuted: boolean;
    isSensorMuted: boolean;
    isGridlineVisible: boolean;
    isScrollActionZoom: boolean;
    zoomLevel: number;
    currentTimepoint: number;
    previouslySelectedWaveform: string;
    selectedWaveform: string;
    selectedSegment: number;
    availableEventTypes: StudyCoughEventType[];
    selectedEventType?: StudyCoughEventType;
    peaksJs: PeaksJs;
    isFixedCommentsDialogOpen: boolean;
    isEventListOpen: boolean;
    isSendForReviewModalOpen: boolean;
    availableFixedComments: CoughOverreadFixedComment[];
    areKeyboardShortcutsVisible: boolean;
    isSessionNotesVisible: boolean;
    isAddingSessionNote: boolean;
    sessionNotes: SessionNote[];
    isDownloadClicked: boolean;
    isHourSegmentsModalOpened: boolean;
    hourSegmentsMap: Map<string, HourBasedSegmentViewModel[]>;
    isAcceptabilityAuditModalVisible: boolean;
    acceptabilityStatus: OverreadStatusEnum;
    isSubmitting: boolean;
    rawTimestampPoints: Map<string, number>;
    storedPlaybackTime?: number;
    canAddEventByDoubleClick: boolean;
    positions: RecordingPositionFilesData;
    comments: CoughAnalysisSetComment[];
    coughAnalysisSet: CoughAnalysisSetClient;
    blindComments: boolean;
    requiresVisualReview: boolean;
    eventDuplicateThreshold: number;
    acceptanceErrorMessage: string;
    isSegmented: boolean;
    visualInspectionErrorMessage: string;
    audioLength: number;
    waveformPoints: PeaksPoint[];
    waveformPeriods: PeaksPeriod[];
    confirmedDuplicatePoints: Set<string>;
}

export interface AnalysisSetState {
    isLoaded: boolean;
    isAnalysisSetDialogOpen: boolean;
    primaryAnalystSetId: string;
    coughAnalysisSets: CoughAnalysisSetsFixedCommentsDisplay[];
    isMaximumAnalysisSetsAlertVisible: boolean;
}

export interface AnalyseState {
    core: AnalyseCoreState;
    analysisSet: AnalysisSetState;
}

export interface IHourBasedSegmentViewModel {
    readonly rawHour: string;
    readonly processedStartTime: number;
    readonly processedEndTime: number;
}

export class HourBasedSegmentViewModel {
    public StartTime: number;
    public EndTime: number;
    public Duration: number;
    public RawHour: string;
    public CoughEventCount: number = 0;

    constructor(viewModel: IHourBasedSegmentViewModel) {
        this.StartTime = viewModel.processedStartTime;
        this.EndTime = viewModel.processedEndTime;
        this.RawHour = viewModel.rawHour;
        this.Duration = this.EndTime - this.StartTime;
    }

    public StartTimeFormatted = () => formatToHHmmss_sss(this.StartTime);
    public EndTimeFormatted = () => formatToHHmmss_sss(this.EndTime);
    public DurationFormatted = () =>
        subtractFormattedTimesWithThousandsOfSec(
            this.EndTimeFormatted(),
            this.StartTimeFormatted()
        );
}
